import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {IBasketRepository} from '@/src/core/domain/repositories'
import {BasketError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import type {BasketData, NewBasketItem} from '@/src/entities/basket/types'
import type {IErrorReporter} from '@/src/ports/services/monitoring'

/**
 * Add Certificate to Basket Use Case
 *
 * Orchestrates adding a certificate product to the basket.
 * Certificates have a custom amount (price) that differs from regular products.
 *
 * Follows Single Responsibility Principle - only handles certificate addition.
 *
 * @example
 * const result = await addCertificateUseCase.execute(certificateId, 5000)
 * if (result.isOk()) {
 *   // Update UI with new basket data
 * } else {
 *   // Handle error
 * }
 */
@injectable()
export class AddCertificateUseCase {
  @inject(DI.IBasketRepository)
  basketRepository!: IBasketRepository

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  async execute(
    certificateId: Id,
    amount?: number,
  ): Promise<OperationResult<BasketData, BasketError>> {
    const item: NewBasketItem = {
      product_id: certificateId,
      quantity: 1,
      amount: amount ?? null,
    }

    try {
      const basketData = await this.basketRepository.add(item)
      return Result.ok(basketData)
    } catch (error) {
      this.errorReporter.capture(
        error instanceof Error ? error : new Error(String(error)),
        {certificate_id: certificateId, amount},
      )
      return Result.fail(new BasketError('ADD_FAILED', error))
    }
  }
}
